// The saloon doors: the island-to-island gate from the Godot build (scripts/islands/
// portal_doorframe.gd), using its authored model (Assets/props/portal_gate/wooden_gate.glb,
// re-exported to public/assets/run/saloon_gate.glb by _src/convert_gate.py).
//
// Shut, it's a weathered timber doorframe with two batwing doors at the end of the street.
// When the island is cleared it comes online: the neon tubes round the doorway stutter alight,
// a portal sheet sweeps up from the sill (gold swirl, cyan rim, CRT scanlines), the sign lights
// up with where it goes, and a light pillar climbs into the sky so you can find it from
// anywhere on the island. The doors swing on their own hinges when a cowboy pushes through,
// and flap shut behind them. Collision is the sim's (two posts + the sign beam, sim/world.ts).

import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { budget } from '../warm';
import { World, GATE } from '../../../sim/world';
import { withHaze } from '../materials';

const BASE = (import.meta as unknown as { env?: { BASE_URL?: string } }).env?.BASE_URL ?? '/';
const MODEL = BASE + 'assets/run/saloon_gate.glb';
let gateModel: Promise<THREE.Object3D> | null = null;

// model space (glTF, metres before GATE.modelScale): width along z, height along y, depth along x
const M_BASE = -0.489; // bottom of the posts
const HINGE_Z = 0.261, HINGE_X = -0.007;
const SIGN_Y = 0.40, SIGN_FACE = 0.086; // the plank's front face

const NEON_ON = new THREE.Color(1.0, 0.7, 0.3).multiplyScalar(2.6);
/** the bonus door burns red: high stakes */
const NEON_BONUS = new THREE.Color(1.0, 0.1, 0.06).multiplyScalar(2.6);
const NEON_OFF = new THREE.Color(0x24343a);

const SURF_VERT = /* glsl */ `
varying vec2 vUv;
void main() { vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 ); }
`;
const SURF_FRAG = /* glsl */ `
varying vec2 vUv;
uniform float uTime; uniform float uOn; uniform float uWarp; uniform vec2 uSize;
uniform vec3 uRim; uniform vec3 uGold; uniform vec3 uDeep;
void main() {
  vec2 m = vUv * uSize;                       // metres across / up the doorway
  float edge = min( min( m.x, uSize.x - m.x ), uSize.y - m.y );
  float rim = exp( -edge * 2.2 );
  vec2 c = m - vec2( uSize.x * 0.5, uSize.y * 0.46 );
  float r = length( c );
  float a = atan( c.y, c.x );
  float sw = 0.5 + 0.5 * sin( a * 3.0 - r * 2.4 + uTime * 2.4 );
  float sw2 = 0.5 + 0.5 * sin( a * 5.0 + r * 3.1 - uTime * 3.3 );
  float bands = smoothstep( 0.45, 0.9, sw ) * 0.75 + smoothstep( 0.6, 0.95, sw2 ) * 0.35;
  float heart = exp( -r * 0.55 );
  float scan = 0.82 + 0.18 * sin( m.y * 34.0 - uTime * 7.0 );
  // the sheet sweeps up from the sill as it comes online
  float front = uOn * ( uSize.y + 1.2 );
  float ignite = smoothstep( front, front - 0.9, m.y );
  float lip = exp( -abs( m.y - front ) * 4.0 ) * step( uOn, 0.999 );
  vec3 col = mix( uDeep, uGold, bands * 0.8 + heart * 0.5 );
  col = mix( col, uRim, clamp( rim * 1.2, 0.0, 1.0 ) );
  // kept well under white so the swirl reads (bloom does the glow); the warp burns it out
  float al = ( 0.16 + 0.36 * bands * ( 0.45 + heart ) + 0.3 * heart + rim * 0.8 ) * scan * ignite + lip * 0.9;
  al *= 1.0 + uWarp * 2.2;
  col = mix( col, vec3( 1.0, 0.95, 0.85 ), clamp( uWarp * 0.6 + lip * 0.6, 0.0, 1.0 ) );
  al = clamp( al, 0.0, 1.1 + uWarp * 0.5 );
  gl_FragColor = vec4( col * al, al );
}
`;

const BEAM_VERT = /* glsl */ `
varying float vH; varying float vSide;
void main() {
  vH = uv.y;
  vec4 wp = modelMatrix * vec4( position, 1.0 );
  vec3 n = normalize( mat3( modelMatrix ) * normal );
  vSide = abs( dot( n, normalize( cameraPosition - wp.xyz ) ) );
  gl_Position = projectionMatrix * viewMatrix * wp;
}
`;
const BEAM_FRAG = /* glsl */ `
varying float vH; varying float vSide;
uniform float uTime; uniform float uOn; uniform vec3 uCol;
void main() {
  float fall = pow( 1.0 - vH, 1.6 );
  float pulse = 0.75 + 0.25 * sin( uTime * 2.0 - vH * 18.0 );
  float al = fall * pulse * pow( vSide, 1.5 ) * uOn * 0.55;
  gl_FragColor = vec4( uCol * al, al );
}
`;

interface Leaf { pivot: THREE.Object3D; side: number; ang: number; vel: number }

export class Gate {
  readonly group = new THREE.Group();
  private gx: number; private gy: number; private gz: number;
  private k = GATE.modelScale;
  private leaves: Leaf[] = [];
  private neonOn: THREE.Color;
  readonly bonus: boolean;
  private neonMat: THREE.MeshBasicMaterial;
  private neonGlowMat: THREE.MeshBasicMaterial;
  private surfU: Record<string, THREE.IUniform>;
  private surf: THREE.Mesh;
  private beamU: Record<string, THREE.IUniform>;
  private beam: THREE.Mesh;
  private signMat: THREE.MeshBasicMaterial;
  private signCanvas: HTMLCanvasElement;
  private signTex: THREE.CanvasTexture;
  private signLabel = '';
  private open = false;
  private tubes!: THREE.Group;
  private openAt = -1;
  private on = 0; // portal sheet sweep 0..1
  private warp = 0;
  private prevX: number[] = [0, 0, 0, 0];
  private havePrev: boolean[] = [false, false, false, false];
  private disposables: { dispose(): void }[] = [];
  private lastTime = 0;
  private pool: THREE.Mesh;
  private poolMat: THREE.MeshBasicMaterial;
  private glow: THREE.PointLight;
  private lamp: THREE.Mesh;
  private lampMat: THREE.MeshBasicMaterial;
  private lampHalo: THREE.Sprite;
  private camDist = 1e9;
  /** the authored frame has loaded (or failed: the neon doorway stands without it) */
  modelReady = false;
  private gone = false;

  /** opts.bonus: the red bonus-round doors at opts.at instead of the street's saloon doors */
  constructor(world: World, time: { value: number }, opts?: { bonus?: boolean; at?: { x: number; y: number; z: number } }) {
    const at = opts?.at;
    this.gx = at ? at.x : world.gate.x; this.gz = at ? at.z : world.gate.z; this.gy = at ? at.y : world.gateY();
    this.bonus = !!opts?.bonus;
    this.neonOn = this.bonus ? NEON_BONUS : NEON_ON;
    const k = this.k, W = GATE.halfW * 2, H = GATE.beamY - 0.05;

    // ---- the authored frame + doors (async)
    // parsed once a session: every island's doors (and the bonus doors) are clones sharing its meshes
    gateModel ??= new GLTFLoader().loadAsync(MODEL).then((g) => g.scene);
    gateModel.then((src) => budget.defer(() => { if (!this.gone) this.onModel(src.clone(true)); this.modelReady = true; }, 'gate:model'),
      (e) => { this.modelReady = true; console.warn('[gate] model failed', e); });

    // ---- neon tubes round the doorway: two uprights and the lintel, inner edge of the frame
    const tubes = new THREE.Group();
    this.neonMat = new THREE.MeshBasicMaterial({ color: NEON_OFF.clone(), toneMapped: false, fog: false });
    this.neonGlowMat = new THREE.MeshBasicMaterial({ color: this.neonOn.clone().multiplyScalar(0.35), transparent: true, opacity: 0, blending: THREE.AdditiveBlending, depthWrite: false, toneMapped: false, fog: false });
    const tubeR = 0.05, glowW = 0.55;
    const mk = (geo: THREE.BufferGeometry, mat: THREE.Material, x: number, y: number, z: number, rz = 0, rx = 0) => {
      const m = new THREE.Mesh(geo, mat); m.position.set(x, y, z); m.rotation.z = rz; m.rotation.x = rx; tubes.add(m); return m;
    };
    const upGeo = new THREE.CylinderGeometry(tubeR, tubeR, H - 0.4, 6, 1);
    const topGeo = new THREE.CylinderGeometry(tubeR, tubeR, W - 0.1, 6, 1);
    const glowUp = new THREE.PlaneGeometry(glowW, H - 0.2);
    const glowTop = new THREE.PlaneGeometry(W + 0.2, glowW);
    this.disposables.push(upGeo, topGeo, glowUp, glowTop);
    for (const fx of [-0.42, 0.42]) {
      for (const s of [-1, 1]) {
        mk(upGeo, this.neonMat, fx, (H - 0.4) / 2 + 0.25, s * (GATE.halfW - 0.03));
        const gl = mk(glowUp, this.neonGlowMat, fx * 1.02, H / 2, s * (GATE.halfW - 0.03));
        gl.rotation.y = Math.PI / 2;
      }
      mk(topGeo, this.neonMat, fx, H - 0.12, 0, 0, Math.PI / 2);
      const gt = mk(glowTop, this.neonGlowMat, fx * 1.02, H - 0.12, 0);
      gt.rotation.y = Math.PI / 2;
    }
    tubes.position.set(this.gx, this.gy, this.gz);
    this.tubes = tubes;
    tubes.renderOrder = 41;
    this.group.add(tubes);

    // ---- the portal sheet filling the doorway
    this.surfU = {
      uTime: time, uOn: { value: 0 }, uWarp: { value: 0 }, uSize: { value: new THREE.Vector2(W, H) },
      uRim: { value: this.bonus ? new THREE.Color(1.0, 0.58, 0.42) : new THREE.Color(1.0, 0.86, 0.55) },
      uGold: { value: this.bonus ? new THREE.Color(1.0, 0.24, 0.1) : new THREE.Color(1.0, 0.78, 0.36) },
      uDeep: { value: this.bonus ? new THREE.Color(0.5, 0.03, 0.02) : new THREE.Color(0.85, 0.42, 0.16) },
    };
    const surfGeo = new THREE.PlaneGeometry(W, H);
    surfGeo.translate(0, H / 2, 0);
    const surfMat = new THREE.ShaderMaterial({
      vertexShader: SURF_VERT, fragmentShader: SURF_FRAG, uniforms: this.surfU,
      transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, side: THREE.DoubleSide,
    });
    this.surf = new THREE.Mesh(surfGeo, surfMat);
    this.surf.position.set(this.gx, this.gy + 0.02, this.gz);
    this.surf.rotation.y = Math.PI / 2;
    this.surf.visible = false;
    this.surf.renderOrder = 40;
    this.group.add(this.surf);
    this.disposables.push(surfGeo, surfMat);

    // ---- the finder beacon: a light pillar you can see from anywhere on the island
    this.beamU = { uTime: time, uOn: { value: 0 }, uCol: { value: this.bonus ? new THREE.Color(1.0, 0.3, 0.16) : new THREE.Color(1.0, 0.82, 0.45) } };
    const beamGeo = new THREE.CylinderGeometry(1.1, 1.6, 170, 16, 1, true);
    beamGeo.translate(0, 85, 0);
    const beamMat = new THREE.ShaderMaterial({
      vertexShader: BEAM_VERT, fragmentShader: BEAM_FRAG, uniforms: this.beamU,
      transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, side: THREE.DoubleSide,
    });
    this.beam = new THREE.Mesh(beamGeo, beamMat);
    this.beam.position.set(this.gx + 1.5, this.gy + GATE.beamY + 1, this.gz);
    this.beam.visible = false;
    this.beam.frustumCulled = false;
    this.group.add(this.beam);
    this.disposables.push(beamGeo, beamMat);

    // ---- the sign: painted lettering on the board, lit when the gate is live
    this.signCanvas = document.createElement('canvas');
    this.signCanvas.width = 512; this.signCanvas.height = 160;
    this.signTex = new THREE.CanvasTexture(this.signCanvas);
    this.signTex.colorSpace = THREE.SRGBColorSpace;
    this.signTex.anisotropy = 4;
    this.signMat = new THREE.MeshBasicMaterial({ map: this.signTex, transparent: true, depthWrite: false, toneMapped: false, polygonOffset: true, polygonOffsetFactor: -2 });
    const signGeo = new THREE.PlaneGeometry(0.62 * k, 0.17 * k);
    for (const s of [-1, 1]) {
      const sign = new THREE.Mesh(signGeo, this.signMat);
      sign.position.set(this.gx + s * (SIGN_FACE * k + 0.02), this.gy + (SIGN_Y - M_BASE) * k, this.gz);
      sign.rotation.y = s < 0 ? -Math.PI / 2 : Math.PI / 2;
      this.group.add(sign);
    }
    this.disposables.push(signGeo, this.signMat, this.signTex);
    this.paintSign(this.bonus ? 'BONUS ROUND' : 'CLOSED', 'CLEAR THE ISLAND', false);

    // ---- a warm pool of light spilling across the ground both sides of the doorway, and the
    // light itself (it backlights the doors so they go dark against the sheet)
    const pc = document.createElement('canvas'); pc.width = pc.height = 128;
    const pg = pc.getContext('2d')!;
    const grad = pg.createRadialGradient(64, 64, 0, 64, 64, 64);
    if (this.bonus) { grad.addColorStop(0, 'rgba(255,90,60,1)'); grad.addColorStop(0.4, 'rgba(255,50,30,0.45)'); grad.addColorStop(1, 'rgba(200,20,10,0)'); }
    else { grad.addColorStop(0, 'rgba(255,190,90,1)'); grad.addColorStop(0.4, 'rgba(255,150,60,0.45)'); grad.addColorStop(1, 'rgba(255,120,40,0)'); }
    pg.fillStyle = grad; pg.fillRect(0, 0, 128, 128);
    const poolTex = new THREE.CanvasTexture(pc); poolTex.colorSpace = THREE.SRGBColorSpace;
    this.poolMat = new THREE.MeshBasicMaterial({ map: poolTex, transparent: true, opacity: 0, blending: THREE.AdditiveBlending, depthWrite: false, toneMapped: false, polygonOffset: true, polygonOffsetFactor: -4 });
    const poolGeo = new THREE.PlaneGeometry(11, W + 3);
    poolGeo.rotateX(-Math.PI / 2);
    this.pool = new THREE.Mesh(poolGeo, this.poolMat);
    this.pool.position.set(this.gx, this.gy + 0.06, this.gz);
    this.pool.visible = false;
    this.group.add(this.pool);
    this.glow = new THREE.PointLight(this.bonus ? 0xff3a20 : 0xffa850, 0, 12, 1.6);
    this.glow.position.set(this.gx, this.gy + 1.6, this.gz);
    this.group.add(this.glow);
    this.disposables.push(poolTex, this.poolMat, poolGeo);

    // ---- closed: a red stop lamp hung under the lintel, slow blink
    this.lampMat = new THREE.MeshBasicMaterial({ color: new THREE.Color(1.0, 0.12, 0.06).multiplyScalar(2), toneMapped: false });
    const lampGeo = new THREE.SphereGeometry(0.16, 12, 8);
    this.lamp = new THREE.Mesh(lampGeo, this.lampMat);
    this.lamp.position.set(this.gx, this.gy + H - 0.35, this.gz);
    this.group.add(this.lamp);
    // its iron bracket up to the lintel
    const rodGeo = new THREE.CylinderGeometry(0.025, 0.025, 0.5, 5, 1);
    const rodMat = new THREE.MeshStandardMaterial({ color: 0x2a2420, roughness: 0.7, metalness: 0.5 });
    const rod = new THREE.Mesh(rodGeo, rodMat);
    rod.position.set(0, 0.32, 0);
    this.lamp.add(rod);
    this.disposables.push(rodGeo, rodMat);
    const hc = document.createElement('canvas'); hc.width = hc.height = 64;
    const hg = hc.getContext('2d')!;
    const hgr = hg.createRadialGradient(32, 32, 0, 32, 32, 32);
    hgr.addColorStop(0, 'rgba(255,80,50,1)'); hgr.addColorStop(1, 'rgba(255,40,20,0)');
    hg.fillStyle = hgr; hg.fillRect(0, 0, 64, 64);
    const haloTex = new THREE.CanvasTexture(hc);
    const haloMat = new THREE.SpriteMaterial({ map: haloTex, blending: THREE.AdditiveBlending, depthWrite: false, transparent: true, toneMapped: false });
    this.lampHalo = new THREE.Sprite(haloMat);
    this.lampHalo.scale.setScalar(1.4);
    this.lampHalo.position.copy(this.lamp.position);
    this.group.add(this.lampHalo);
    this.disposables.push(lampGeo, this.lampMat, haloTex, haloMat);
  }

  private onModel(root: THREE.Object3D) {
    const k = this.k;
    root.scale.setScalar(k);
    root.position.set(this.gx, this.gy - M_BASE * k, this.gz);
    root.updateMatrixWorld(true);
    const doors: THREE.Mesh[] = [];
    root.traverse((o) => {
      const m = o as THREE.Mesh;
      if (!m.isMesh) return;
      m.castShadow = true; m.receiveShadow = true;
      const mat = m.material as THREE.MeshStandardMaterial;
      // the materials and meshes are the shared model's (never disposed): hazed once, for every clone
      if (mat && !(mat.userData as { hazed?: boolean }).hazed) {
        mat.roughness = 0.92; mat.metalness = 0;
        withHaze(mat, 0.55);
        mat.userData.hazed = true;
      }
      if (m.name.startsWith('door')) doors.push(m);
    });
    // hang each door on its own hinge so it swings
    for (const d of doors) {
      d.geometry.computeBoundingBox();
      const bb = d.geometry.boundingBox!;
      const side = (bb.min.z + bb.max.z) / 2 < 0 ? -1 : 1; // which half of the doorway it covers
      const pivot = new THREE.Object3D();
      const parent = d.parent!;
      pivot.position.set(d.position.x + HINGE_X, d.position.y, d.position.z + side * HINGE_Z);
      parent.add(pivot);
      d.position.set(-HINGE_X, 0, -side * HINGE_Z);
      pivot.add(d);
      this.leaves.push({ pivot, side, ang: 0, vel: 0 });
    }
    this.group.add(root);
  }

  private paintSign(top: string, bottom: string, lit: boolean) {
    const key = `${top}|${bottom}|${lit}`;
    if (key === this.signLabel) return;
    this.signLabel = key;
    const c = this.signCanvas, g = c.getContext('2d')!;
    g.clearRect(0, 0, c.width, c.height);
    g.textAlign = 'center'; g.textBaseline = 'middle';
    g.lineJoin = 'round';
    const ink = 'rgba(28,18,12,0.85)';
    const paint = lit ? '#fff4d6' : '#d9c7a0';
    g.font = '700 58px "Space Grotesk", sans-serif';
    g.lineWidth = 9; g.strokeStyle = ink; g.strokeText(top, 256, 56);
    g.fillStyle = paint; g.fillText(top, 256, 56);
    g.font = '600 34px "Space Grotesk", sans-serif';
    g.lineWidth = 7; g.strokeText(bottom, 256, 118);
    g.fillStyle = lit ? (this.bonus ? '#ff7a58' : '#ffd27a') : '#c9b48c'; g.fillText(bottom, 256, 118);
    this.signTex.needsUpdate = true;
  }

  /** Back-compat with the first build: charge ≥ 1 means open. */
  setCharge(v: number) { this.setState(v >= 0.99, 0, ''); }

  /** open: the island is cleared; warp 0..1: the posse warp is spooling; label: where it goes. */
  setState(open: boolean, warp: number, label: string) {
    if (open && !this.open) this.openAt = this.lastTime;
    if (!open) { this.openAt = -1; }
    this.open = open;
    this.warp = warp;
    if (this.bonus) this.paintSign('BONUS ROUND', open ? (label || 'HIGH STAKES') : 'CLEAR THE ISLAND', open);
    else this.paintSign(open ? 'NEXT STOP' : 'CLOSED', open ? (label || 'ALL ABOARD') : 'CLEAR THE ISLAND', open);
  }

  update(time: number, pushers: readonly { x: number; y: number; z: number }[] = [], dt = 1 / 60, camPos?: THREE.Vector3) {
    this.lastTime = time;
    dt = Math.min(0.05, Math.max(0, dt));
    if (camPos) this.camDist = Math.hypot(camPos.x - this.gx, camPos.z - this.gz);

    // ---- ignition: neon stutters alight, then the sheet sweeps up from the sill
    const since = this.openAt >= 0 ? time - this.openAt : -1;
    let neon = 0;
    if (this.open && since >= 0) {
      if (since < 1.1) {
        const f = Math.sin(since * 61.0) * Math.sin(since * 23.0 + 1.3);
        neon = f > 0.1 - since * 0.6 ? 0.4 + since * 0.5 : 0.05;
      } else neon = 0.9 + 0.1 * Math.sin(time * 37.0) * Math.sin(time * 5.3);
      this.on = Math.min(1, this.on + dt / 1.1 * (since > 0.4 ? 1 : 0));
    } else {
      this.on = Math.max(0, this.on - dt * 2);
    }
    this.neonMat.color.copy(NEON_OFF).lerp(this.neonOn, neon);
    // dark, the tubes would read as scaffolding: they only exist once the gate is powered
    this.tubes.visible = this.open || this.on > 0;
    this.neonGlowMat.opacity = neon * 0.55;
    this.surfU.uOn.value = this.on;
    this.surfU.uWarp.value = this.warp;
    this.surf.visible = this.on > 0.001;
    this.signMat.color.setScalar(this.open ? 1.25 + 0.15 * neon : 0.85);
    this.poolMat.opacity = this.on * (0.55 + 0.1 * Math.sin(time * 2.4)) * (1 + this.warp);
    this.pool.visible = this.on > 0.001;
    this.glow.intensity = this.on * (14 + 3 * Math.sin(time * 2.4)) * (1 + this.warp * 2);
    const blink = 0.55 + 0.45 * Math.max(0, Math.sin(time * 2.2));
    this.lamp.visible = this.lampHalo.visible = !this.open;
    this.lampMat.color.setRGB(1.0 * 2 * blink, 0.12 * 2 * blink, 0.06 * 2 * blink);
    this.lampHalo.material.opacity = blink;
    // the beacon fades out as you arrive, so it never blinds you at the door
    const near = Math.min(1, Math.max(0, (this.camDist - 14) / 30));
    this.beamU.uOn.value = this.on * near * (1 + this.warp);
    this.beam.visible = (this.beamU.uOn.value as number) > 0.002;

    // ---- the doors: pushed by whoever walks through, sprung shut behind them
    const n = Math.min(4, pushers.length);
    const target = [0, 0];
    for (let i = 0; i < n; i++) {
      const p = pushers[i];
      const dx = p.x - this.gx, dz = p.z - this.gz, dy = p.y - this.gy;
      const vx = this.havePrev[i] ? (p.x - this.prevX[i]) / Math.max(1e-3, dt) : 0;
      this.prevX[i] = p.x; this.havePrev[i] = true;
      if (Math.abs(dx) > 2.4 || Math.abs(dz) > GATE.halfW + 0.4 || dy < -1 || dy > 4.5) continue;
      // swing the way they're going (or away from them if they're standing in it)
      const dir = Math.abs(vx) > 0.3 ? Math.sign(vx) : -Math.sign(dx || 1);
      const amount = Math.min(1.9, 0.25 + Math.abs(vx) * 0.22) * (1 - Math.abs(dx) / 2.6);
      const both = Math.abs(dz) < GATE.halfW * 0.35;
      for (let l = 0; l < this.leaves.length; l++) {
        const lf = this.leaves[l];
        if (!both && Math.sign(dz) !== lf.side) continue;
        if (Math.abs(amount) > Math.abs(target[l])) target[l] = dir * amount;
      }
    }
    // the warp blows them wide open
    for (let l = 0; l < this.leaves.length; l++) {
      const lf = this.leaves[l];
      const want = this.warp > 0 ? 1.7 : target[l];
      const kSpring = want !== 0 ? 30 : 14, damp = want !== 0 ? 7 : 2.4; // shut loosely: they flap
      lf.vel += ((want - lf.ang) * kSpring - lf.vel * damp) * dt;
      lf.ang = Math.max(-1.95, Math.min(1.95, lf.ang + lf.vel * dt));
      // a leaf hinged on -z opens toward +x with +angle; its partner mirrors
      lf.pivot.rotation.y = lf.side < 0 ? lf.ang : -lf.ang;
    }
  }

  dispose() { this.gone = true; for (const d of this.disposables) d.dispose(); }
}
